import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { getToken } from "../../utils/tokenStorage";
import { url } from "../../utils/config";
import { delayMessage } from "../../utils/delayMessage";
import { serverMessage } from "../../utils/serverMessage";

interface StatState {
  countPending: string;
  countInterview: string;
  countDecline: string;
  statLoading: boolean;
  statusBasedJobs: any[];
  resetJobsArray: any[];
  statusSearch: {
    search: any;
    statusValue: any;
    typeValue: any;
  };
  pendingRequestTimer: any; // Adjust the type here
  currentStatusPage: number;
  pageStatusSize: number;
  totalPages: number;
}

const initialState: StatState = {
  countPending: "0",
  countInterview: "0",
  countDecline: "0",
  statLoading: false,
  statusBasedJobs: [],
  resetJobsArray: [],
  statusSearch: {
    search: "",
    statusValue: "",
    typeValue: "",
  },
  pendingRequestTimer: null,
  currentStatusPage: 1,
  pageStatusSize: 10,
  totalPages: 0,
};

const statusJobs = async (
  status: string,
  currentStatusPage: string,
  pageStatusSize: string
) => {
  const token = getToken();
  const data = await axios.get(
    `${url.baseUrl}/${status}?page=${currentStatusPage}&size=${pageStatusSize}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

export const pendingCount = createAsyncThunk(
  "pendingCount",
  async ({ currentStatusPage, pageStatusSize }: any, thunkAPI) => {
    try {
      const { data } = await statusJobs(
        "pending",
        currentStatusPage,
        pageStatusSize
      );
      // console.log(data);
      return data;
    } catch (error: any) {
      console.log(error.response);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const interviewCount = createAsyncThunk(
  "interviewCount",
  async ({ currentStatusPage, pageStatusSize }: any, thunkAPI) => {
    try {
      const { data } = await statusJobs(
        "interview",
        currentStatusPage,
        pageStatusSize
      );
      // console.log(data);
      return data;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const declinedCount = createAsyncThunk(
  "declinedCount",
  async ({ currentStatusPage, pageStatusSize }: any, thunkAPI) => {
    try {
      const { data } = await statusJobs(
        "decline",
        currentStatusPage,
        pageStatusSize
      );
      // console.log(data);
      return data;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const StatSlice = createSlice({
  name: "pending",
  initialState,
  reducers: {
    collectSearchFields: (state, { payload }) => {
      const { name, value } = payload;
      state.statusSearch = { ...state.statusSearch, [name]: value };
    },
    clearSearchFields: (state) => {
      state.statusSearch.search = "";
      state.statusSearch.statusValue = "";
      state.statusSearch.typeValue = "";
    },

    increasePage: (state) => {
      state.currentStatusPage = state.currentStatusPage + 1;
    },
    decreasePage: (state) => {
      state.currentStatusPage = state.currentStatusPage - 1;
    },
    jobsPerPage: (state, { payload }) => {
      state.pageStatusSize = payload;
    },

    searchStatusJobs: (state, { payload }) => {
      const { search, statusValue, typeValue } = payload;
      let filteredJobs = state.resetJobsArray.slice();

      // Filter by input search
      filteredJobs = filteredJobs.filter((each) => {
        return (
          each.company.toLowerCase().includes(search.toLowerCase()) ||
          each.position.toLowerCase().includes(search.toLowerCase())
        );
      });

      // Filter by job status
      filteredJobs = filteredJobs.filter((each) => {
        if (statusValue) {
          return each.jobStatus === statusValue;
        }
        return each;
      });
      // console.log(filteredJobs);

      // Filter by job type
      filteredJobs = filteredJobs.filter((each) => {
        if (typeValue) {
          return each.jobType === typeValue;
        }
        return each;
      });
      // console.log(filteredJobs);

      state.statusBasedJobs = filteredJobs;
    },
  },

  extraReducers: (builder) => {
    builder
      // Pending Jobs Count
      .addCase(pendingCount.pending, (state) => {
        state.statLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(pendingCount.fulfilled, (state, { payload }: any) => {
        state.statLoading = false;
        state.countPending = payload.data.count;
        state.statusBasedJobs = payload.data.jobList;
        state.resetJobsArray = payload.data.jobList;
        state.totalPages = payload.data.totalPages;
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })
      .addCase(pendingCount.rejected, (state, { payload }) => {
        state.statLoading = false;
        serverMessage(payload);
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })

      // Interview Jobs Count
      .addCase(interviewCount.pending, (state) => {
        state.statLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(interviewCount.fulfilled, (state, { payload }: any) => {
        state.statLoading = false;
        state.countInterview = payload.data.count;
        state.statusBasedJobs = payload.data.jobList;
        state.resetJobsArray = payload.data.jobList;
        state.totalPages = payload.data.totalPages;
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })
      .addCase(interviewCount.rejected, (state, { payload }) => {
        state.statLoading = false;
        serverMessage(payload);
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })

      // Declined Jobs Count
      .addCase(declinedCount.pending, (state) => {
        state.statLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
        }
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(declinedCount.fulfilled, (state, { payload }: any) => {
        state.statLoading = false;
        state.countDecline = payload.data.count;
        state.statusBasedJobs = payload.data.jobList;
        state.resetJobsArray = payload.data.jobList;
        state.totalPages = payload.data.totalPages;
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      })
      .addCase(declinedCount.rejected, (state, { payload }) => {
        state.statLoading = false;
        serverMessage(payload);
        // Clear timeout
        if (state.pendingRequestTimer) {
          clearTimeout(state.pendingRequestTimer);
          state.pendingRequestTimer = null; // Reset the timer ID
        }
      });
  },
});

export default StatSlice.reducer;

export const {
  collectSearchFields,
  clearSearchFields,
  searchStatusJobs,
  increasePage,
  decreasePage,
  jobsPerPage,
} = StatSlice.actions;
